import React, { useMemo, useState } from 'react';
import { apiPost } from '../utils/api';
import { BTN } from '../constants/buttons';
import { MODAL_OVERLAY, MODAL_PANEL_MOBILE_CAP } from '../constants/overlayLayers';

/**
 * RemoveUserFromProductionModal — production-scoped remove.
 *
 * Two flows in one component, picked by whether `productionId` is supplied:
 *
 *   1. Buyer / production-admin context: productionId + productionName passed in.
 *      The modal jumps straight to a single-production confirm dialog.
 *      No chooser, no other-production info — that's the privacy boundary.
 *
 *   2. Site-admin context: productionId null/undefined. The modal renders a
 *      picker step first, listing every production the target user is a member
 *      of (drawn from `user.departments`). Admin picks one, then the same
 *      confirm dialog runs. Admins legitimately need cross-production visibility
 *      so the picker is intentional here.
 *
 * Removal hits POST /api/departments/production/:productionId/remove-member/:userId.
 * Server cleanupDepartmentRemoval (server/helpers/departmentHelpers.js) auto-
 * reassigns approval steps to another buyer in the dept (or skips the step if
 * no replacement exists), so this modal does not need its own replacement
 * picker.
 *
 * Props:
 *   user: { id: number, display_name: string, departments?: Array<{
 *     department_id, department_name, production_id, production_name, role
 *   }> }
 *   productionId?: number       (optional — omit for admin picker flow)
 *   productionName?: string     (optional — paired with productionId)
 *   onClose: () => void
 *   onSuccess: (data) => void
 */
export default function RemoveUserFromProductionModal({
  user,
  productionId,
  productionName,
  onClose,
  onSuccess,
}) {
  // Derive the user's unique productions for the picker step.
  // Empty list if user.departments isn't populated (defensive — caller
  // should pass it in the admin flow but handle gracefully if not).
  const productions = useMemo(() => {
    const seen = new Map();
    for (const d of user.departments || []) {
      if (d.production_id == null || seen.has(d.production_id)) continue;
      seen.set(d.production_id, {
        id: d.production_id,
        name: d.production_name || `Production ${d.production_id}`,
        deptCount: 0,
      });
    }
    // Count departments per production
    for (const d of user.departments || []) {
      const p = seen.get(d.production_id);
      if (p) p.deptCount += 1;
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [user.departments]);

  // Resolved production target. Starts populated when the caller passed
  // productionId; otherwise null until admin picks one in the picker step.
  const [picked, setPicked] = useState(
    productionId ? { id: productionId, name: productionName || `Production ${productionId}` } : null
  );

  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);

  async function handleConfirm() {
    if (!picked) return;
    setExecuting(true);
    setError(null);
    try {
      const res = await apiPost(
        `/api/departments/production/${picked.id}/remove-member/${user.id}`,
        {}
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Removal failed');
      onSuccess?.(data);
      onClose();
    } catch (err) {
      setError(err.message);
      setExecuting(false);
    }
  }

  // ── Picker step (admin flow only) ─────────────────────────────────────
  if (!picked) {
    return (
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 ${MODAL_OVERLAY}`}
        onClick={onClose}
        data-testid="remove-from-production-modal"
      >
        <div
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full ${MODAL_PANEL_MOBILE_CAP}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
              Remove {user.display_name} — pick a production
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Which production should they be removed from? Removal only affects the chosen production.
            </p>
          </div>

          <div className="p-6 space-y-2">
            {productions.length === 0 ? (
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-sm text-amber-700 dark:text-amber-300">
                {user.display_name} isn't a member of any production. There's nothing to remove.
              </div>
            ) : (
              productions.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPicked({ id: p.id, name: p.name })}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors text-left"
                  data-testid={`remove-from-production-pick-${p.id}`}
                >
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {p.name}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {p.deptCount} dept{p.deptCount === 1 ? '' : 's'}
                  </span>
                </button>
              ))
            )}
          </div>

          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <button type="button" onClick={onClose} className={BTN.secondary}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Confirm step (buyer flow direct, admin flow after picking) ───────
  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 ${MODAL_OVERLAY}`}
      onClick={onClose}
      data-testid="remove-from-production-modal"
    >
      <div
        className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full ${MODAL_PANEL_MOBILE_CAP}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            Remove {user.display_name} from this production?
          </h3>
        </div>

        <div className="p-6 space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This will remove <strong className="text-gray-800 dark:text-gray-200">{user.display_name}</strong>{' '}
            from every department in{' '}
            <strong className="text-gray-800 dark:text-gray-200">{picked.name}</strong>. They keep their
            account and any other assignments.
          </p>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-sm text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
              {error}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-between gap-2">
          {/* Allow stepping back to the picker — only visible if the admin
              entered via the picker (i.e. productionId wasn't fixed by the
              caller). */}
          {!productionId ? (
            <button
              type="button"
              onClick={() => { setPicked(null); setError(null); }}
              className={BTN.secondary}
              disabled={executing}
              data-testid="remove-from-production-back"
            >
              ← Pick different
            </button>
          ) : (
            <span />
          )}

          <div className="flex gap-2">
            <button type="button" onClick={onClose} className={BTN.secondary} disabled={executing}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={executing}
              className={`${BTN.danger} ${executing ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="remove-from-production-confirm"
            >
              {executing ? 'Removing…' : 'Remove'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
